import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Apartment,
  ApartmentDetails,
  ApartmentRentalState,
  MarkerData,
  ApartmentSortMode,
} from "../../../types/apartments";

const initialState: ApartmentRentalState = {
  locationApartmentList: [],
  selectedApartment: null,
  step: 0,
  nextEnabled: false,
  isMapOpen: false,
  apartmentDetails: null,
  numberOfGuest: 1,
  selectedMarker: null,
  sortMode: "PRICE_ASC",
};

const apartmentRentalSlice = createSlice({
  name: "apartmentRental",
  initialState,
  reducers: {
    setAllApartment: (state, action: PayloadAction<Apartment[]>) => {
      // Defensive: Guard in case the action payload is unexpectedly undefined / null
      if (!action.payload || !Array.isArray(action.payload)) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("setAllApartment: Payload is empty or not an array – setting empty list.", action.payload);
        }
        state.locationApartmentList = [];
        return; // Nothing to sort
      }

      state.locationApartmentList = action.payload.slice();
      // apply current sorting mode immediately for consistent initial display
      if (state.sortMode && state.locationApartmentList.length > 0) {
        const mode = state.sortMode;
        const collator = new Intl.Collator(undefined, { sensitivity: "base" });
        state.locationApartmentList.sort((a, b) => {
          const ap = typeof a.price === "number" ? a.price : Number(a.price) || 0;
          const bp = typeof b.price === "number" ? b.price : Number(b.price) || 0;
          switch (mode) {
            case "PRICE_ASC":
              return ap === bp ? collator.compare(a.name, b.name) : ap - bp;
            case "PRICE_DESC":
              return ap === bp ? collator.compare(a.name, b.name) : bp - ap;
            case "NAME_DESC":
              return collator.compare(b.name, a.name);
            case "NAME_ASC":
            default:
              return collator.compare(a.name, b.name);
          }
        });
      }
    },
    setIsMapOpen: (state, action: PayloadAction<boolean>) => {
      state.isMapOpen = action.payload;
    },
    setSelectedApartment: (state, action: PayloadAction<Apartment>) => {
      state.selectedApartment = action.payload;
    },
    navigatedToPreviousStep: (state) => {
      if (state.isMapOpen) {
        state.isMapOpen = false;
      }
      state.step -= 1;
    },
    setNextButtonClick: (state) => {
      state.step += 1;
    },
    setApartmentDetails: (state, action: PayloadAction<ApartmentDetails>) => {
      state.apartmentDetails = action.payload;
    },
    setNumberOfGuest: (state, action: PayloadAction<number>) => {
      state.numberOfGuest = action.payload;
    },
    setSelectedMarker: (state, action: PayloadAction<MarkerData>) => {
      state.selectedMarker = action.payload;
    },
    sortApartment: (state, action: PayloadAction<ApartmentSortMode | "ASC" | "DESC">) => {
      // Backwards compatibility: map legacy values
      const legacy = action.payload === "ASC" || action.payload === "DESC";
      const mode: ApartmentSortMode = legacy
        ? action.payload === "ASC"
          ? "PRICE_ASC"
          : "PRICE_DESC"
        : (action.payload as ApartmentSortMode);
      state.sortMode = mode;
      const list = state.locationApartmentList;
      const collator = new Intl.Collator(undefined, { sensitivity: "base" });
      list.sort((a, b) => {
        switch (mode) {
          case "PRICE_ASC": {
            const ap = typeof a.price === "number" ? a.price : Number(a.price) || 0;
            const bp = typeof b.price === "number" ? b.price : Number(b.price) || 0;
            if (ap === bp) return collator.compare(a.name, b.name); // tie-breaker
            return ap - bp;
          }
          case "PRICE_DESC": {
            const ap = typeof a.price === "number" ? a.price : Number(a.price) || 0;
            const bp = typeof b.price === "number" ? b.price : Number(b.price) || 0;
            if (ap === bp) return collator.compare(a.name, b.name);
            return bp - ap;
          }
          case "NAME_DESC":
            return collator.compare(b.name, a.name);
          case "NAME_ASC":
          default:
            return collator.compare(a.name, b.name);
        }
      });
    },
    bookingCreationCompleted: () => initialState,
    setVenueReservationSuccess: (state, action: PayloadAction<number>) => {
      console.log("action.payload", action.payload);
      state.step = action.payload;
    },
  },
 
});

export const {
  setAllApartment,
  navigatedToPreviousStep,
  setSelectedMarker,
  setSelectedApartment,
  setNextButtonClick,
  setIsMapOpen,
  setApartmentDetails,
  setNumberOfGuest,
  bookingCreationCompleted,
  sortApartment,
  setVenueReservationSuccess
} = apartmentRentalSlice.actions;
export default apartmentRentalSlice.reducer;
